import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { Camera, ImagePlus, Trash2, Loader2, ImageOff, X } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";

// Kecilkan foto HP (bisa 5-10 MB) jadi JPEG maks 1280px sebelum diunggah
export const compressImage = (file, maxSize = 1280, quality = 0.8) =>
  new Promise((resolve) => {
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      const scale = Math.min(1, maxSize / Math.max(img.width, img.height));
      const canvas = document.createElement("canvas");
      canvas.width = Math.round(img.width * scale);
      canvas.height = Math.round(img.height * scale);
      canvas.getContext("2d").drawImage(img, 0, 0, canvas.width, canvas.height);
      URL.revokeObjectURL(url);
      canvas.toBlob((b) => resolve(b || file), "image/jpeg", quality);
    };
    img.onerror = () => { URL.revokeObjectURL(url); resolve(file); };
    img.src = url;
  });

export const uploadFoto = async (endpoint, file) => {
  const blob = await compressImage(file);
  const fd = new FormData();
  fd.append("file", blob, "foto.jpg");
  return axios.post(`${BACKEND_URL}${endpoint}`, fd, { withCredentials: true });
};

export const Lightbox = ({ src, onClose }) => (
  <div className="fixed inset-0 z-[80] bg-black/90 flex items-center justify-center p-4" onClick={onClose} data-testid="photo-lightbox">
    <button onClick={onClose} className="absolute top-4 right-4 text-white/80 hover:text-white" data-testid="photo-lightbox-close"><X className="w-6 h-6" /></button>
    <img src={src} alt="Foto" className="max-w-full max-h-full object-contain rounded-lg" />
  </div>
);

export const AuthImage = ({ path, alt, className = "", emptyText = "Belum ada foto", testid }) => {
  const [src, setSrc] = useState(null);
  const [state, setState] = useState(path ? "loading" : "empty");
  const [zoom, setZoom] = useState(false);

  useEffect(() => {
    if (!path) { setSrc(null); setState("empty"); return undefined; }
    let url = null;
    let alive = true;
    setState("loading");
    axios.get(`${BACKEND_URL}/api/files/${path}`, { withCredentials: true, responseType: "blob" })
      .then((r) => { if (alive) { url = URL.createObjectURL(r.data); setSrc(url); setState("ok"); } })
      .catch(() => { if (alive) setState("error"); });
    return () => { alive = false; if (url) URL.revokeObjectURL(url); };
  }, [path]);

  if (state !== "ok") {
    return (
      <div className={`flex flex-col items-center justify-center gap-1 bg-slate-900 border border-dashed border-slate-700 text-slate-500 text-[11px] ${className}`} data-testid={testid}>
        {state === "loading" ? <Loader2 className="w-5 h-5 animate-spin" /> : <ImageOff className="w-5 h-5" />}
        <span>{state === "loading" ? "Memuat foto..." : state === "error" ? "Foto gagal dimuat" : emptyText}</span>
      </div>
    );
  }
  return (
    <>
      <img src={src} alt={alt} onClick={() => setZoom(true)} className={`object-cover cursor-zoom-in ${className}`} data-testid={testid} />
      {zoom && <Lightbox src={src} onClose={() => setZoom(false)} />}
    </>
  );
};

// Pilih foto dari galeri HP atau ambil langsung dari kamera
export const PhotoPicker = ({ label, existingPath, file, onFileChange, onRemoveExisting, testid }) => {
  const galleryRef = useRef(null);
  const cameraRef = useRef(null);
  const [preview, setPreview] = useState(null);

  useEffect(() => {
    if (!file) { setPreview(null); return undefined; }
    const u = URL.createObjectURL(file);
    setPreview(u);
    return () => URL.revokeObjectURL(u);
  }, [file]);

  const pick = (e) => { const f = e.target.files?.[0]; if (f) onFileChange(f); e.target.value = ""; };
  const btn = "flex-1 py-2 rounded-xl text-[11px] font-bold flex items-center justify-center gap-1.5 border transition-colors";

  return (
    <div data-testid={testid}>
      <label className="text-xs font-semibold text-slate-300 block mb-1">{label}</label>
      {preview ? (
        <img src={preview} alt="Pratinjau" className="w-full h-40 object-cover rounded-xl border border-slate-700" data-testid={`${testid}-preview`} />
      ) : (
        <AuthImage path={existingPath} alt={label} className="w-full h-40 rounded-xl" testid={`${testid}-existing`} />
      )}
      <div className="flex gap-2 mt-2">
        <button type="button" onClick={() => galleryRef.current?.click()} className={`${btn} bg-slate-800 border-slate-700 text-slate-200 hover:bg-slate-700`} data-testid={`${testid}-gallery-btn`}>
          <ImagePlus className="w-3.5 h-3.5" />Pilih dari Galeri
        </button>
        <button type="button" onClick={() => cameraRef.current?.click()} className={`${btn} bg-cyan-600/20 border-cyan-600/40 text-cyan-300 hover:bg-cyan-600/40`} data-testid={`${testid}-camera-btn`}>
          <Camera className="w-3.5 h-3.5" />Ambil Foto
        </button>
        {(file || existingPath) && (
          <button type="button" onClick={() => (file ? onFileChange(null) : onRemoveExisting?.())} title="Hapus foto" className={`${btn} flex-none px-3 bg-slate-800 border-slate-700 text-rose-400 hover:bg-rose-900/40`} data-testid={`${testid}-remove-btn`}>
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        )}
      </div>
      <input ref={galleryRef} type="file" accept="image/*" className="hidden" onChange={pick} data-testid={`${testid}-gallery-input`} />
      <input ref={cameraRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={pick} data-testid={`${testid}-camera-input`} />
    </div>
  );
};
